import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Download, Printer, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import ReportBasic from '../components/ReportBasic';
import ReportPremium from '../components/ReportPremium';
import { mockReportBasic, mockReportPremium } from '../data/mockReport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReportPage = () => {
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const contractId = searchParams.get('id') || searchParams.get('contract_id');
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (contractId) {
      // Load real report from backend
      loadReport(contractId);
    } else {
      // Use mock report
      setReport(type === 'premium' ? mockReportPremium : mockReportBasic);
    }
  }, [contractId, type]);
  
  const loadReport = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/analysis/${id}`);
      setReport(response.data);
    } catch (err) {
      console.error('Error loading report:', err);
      if (err.response?.status === 404) {
        setError('Report non ancora disponibile. L\'analisi potrebbe essere ancora in corso: riprova tra qualche minuto.');
      } else {
        setError('Errore nel caricamento del report. Riprova tra qualche istante.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Future: generate PDF
    alert('Download PDF sarà disponibile presto!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-xl font-semibold text-gray-700">Caricamento report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="text-center max-w-lg" data-testid="report-error-state">
          <p className="text-xl font-semibold text-red-600">
            {error || 'Report non trovato'}
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button
              data-testid="report-retry-button"
              onClick={() => loadReport(contractId)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Riprova
            </Button>
            <Button
              variant="outline"
              data-testid="report-back-home-button"
              onClick={() => window.location.href = '/'}
            >
              Torna alla home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      {/* Action Bar - Hide on Print */}
      <div className="max-w-4xl mx-auto mb-6 px-4 print:hidden">
        <div className="bg-white rounded-xl shadow-md p-4 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => window.location.href = '/'}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Torna alla home</span>
          </Button>

          <div className="flex space-x-3">
            <Button
              onClick={handlePrint}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center space-x-2"
            >
              <Printer className="w-4 h-4" />
              <span>Stampa</span>
            </Button>
            <Button
              onClick={handleDownload}
              className="bg-green-600 hover:bg-green-700 text-white flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Scarica PDF</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div className="px-4">
        {type === 'premium' || report.report_type === 'premium' ? (
          <ReportPremium report={report} />
        ) : (
          <ReportBasic report={report} />
        )}
      </div>

      {/* Info Footer - Hide on Print */}
      {!contractId && (
        <div className="max-w-4xl mx-auto mt-8 px-4 print:hidden">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 text-center">
            <p className="text-gray-700 mb-3">
              <span className="font-bold">Questo è un report di esempio.</span> Carica il tuo contratto per ricevere un'analisi personalizzata!
            </p>
            <Button
              onClick={() => window.location.href = '/#upload-section'}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
            >
              Analizza il tuo contratto
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportPage;
